/**
 * React Hooks for Correspondences
 *
 * This module provides custom hooks for managing correspondence state and operations
 * in React components using React Query for caching and synchronization.
 *
 * Query hooks use directApiClient for direct API calls (faster, no server action overhead).
 * Mutation hooks still use server actions for revalidation support.
 */

'use client';

import { useCallback } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { directApiClient } from '@/lib/api/direct-client';
import {
  createCorrespondenceAction,
  bulkCreateCorrespondencesAction,
  updateCorrespondenceAction,
  deleteCorrespondenceAction,
  sendCorrespondenceAction
} from './actions';
import {
  getCorrespondencesCacheKey,
  getCorrespondenceCacheKey,
  formatCorrespondenceType
} from './utils';
import { getDependencies } from '../query-dependencies';
import type { QueryClient } from '@tanstack/react-query';
import type {
  Correspondence,
  CorrespondenceListResponse,
  CreateCorrespondenceRequest,
  BulkCreateCorrespondenceRequest,
  UpdateCorrespondenceRequest,
  CorrespondenceActionResult
} from './types';
import { dispatchCorrespondenceWorkflowCompleted } from '@/lib/events/correspondence-events';

// Error type for correspondence validation failures. Mutation actions return
// `validationErrors`; we attach them to the thrown error so onError can render
// per-field messages.
class CorrespondenceValidationError extends Error {
  validationErrors?: Record<string, string[]>;
  constructor(message: string, validationErrors?: Record<string, string[]>) {
    super(message);
    this.name = 'CorrespondenceValidationError';
    this.validationErrors = validationErrors;
  }
}

// LF-3021 single-source invalidation. Hand-rolled invalidation lists were
// previously sprinkled across every mutation; this helper delegates to the
// `correspondence` resolver in `query-dependencies.ts` so the cascade
// (matter overview, tasks, institutions, inventory, discovery, redemption,
// workflow caches, sidebar counts) can never drift.
function invalidateAfterCorrespondenceMutation(
  queryClient: QueryClient,
  matterId: number,
  correspondenceId?: number
) {
  for (const key of getDependencies(
    'correspondence',
    matterId,
    correspondenceId
  )) {
    queryClient.invalidateQueries({ queryKey: key });
  }
}

// Query Hooks

/**
 * Hook to fetch all correspondences for a matter
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useMatterCorrespondences(
  matterId: number,
  options?: Omit<
    UseQueryOptions<CorrespondenceListResponse, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: getCorrespondencesCacheKey(matterId),
    queryFn: async () => {
      // Direct API call via directApiClient
      const correspondences = await directApiClient.get<Correspondence[]>(
        `/matters/${matterId}/correspondences`
      );

      return {
        correspondences,
        total: correspondences.length
      } as CorrespondenceListResponse;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

/**
 * Hook to fetch a single correspondence
 * Uses directApiClient for direct API calls (no server action overhead)
 */
export function useCorrespondence(
  matterId: number,
  correspondenceId: number,
  options?: Omit<UseQueryOptions<Correspondence, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: getCorrespondenceCacheKey(matterId, correspondenceId),
    queryFn: async () => {
      // Direct API call via directApiClient
      return directApiClient.get<Correspondence>(
        `/matters/${matterId}/correspondences/${correspondenceId}`
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

// Mutation Hooks

/**
 * Hook to create a new correspondence
 */
export function useCreateCorrespondence(
  matterId: number,
  options?: UseMutationOptions<
    CorrespondenceActionResult<Correspondence>,
    Error,
    CreateCorrespondenceRequest
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCorrespondenceRequest) => {
      const result = await createCorrespondenceAction(matterId, data);
      if (!result.success) {
        throw new CorrespondenceValidationError(
          result.error || 'Failed to create correspondence',
          result.validationErrors
        );
      }
      return result;
    },
    onSuccess: (result) => {
      invalidateAfterCorrespondenceMutation(
        queryClient,
        matterId,
        result.data?.id
      );

      const correspondence = result.data!;
      toast.success(
        `${formatCorrespondenceType(correspondence.type)} created successfully`
      );
    },
    onError: (error: Error) => {
      if (
        error instanceof CorrespondenceValidationError &&
        error.validationErrors
      ) {
        // Show validation errors
        const errors = Object.entries(error.validationErrors)
          .map(
            ([field, messages]) =>
              `${field}: ${(messages as string[]).join(', ')}`
          )
          .join('\n');
        toast.error(`Validation failed:\n${errors}`);
      } else {
        toast.error(error.message || 'Failed to create correspondence');
      }
    },
    ...options
  });
}

/**
 * Hook to bulk create correspondences
 */
export function useBulkCreateCorrespondences(
  matterId: number,
  options?: UseMutationOptions<
    CorrespondenceActionResult<Correspondence[]>,
    Error,
    BulkCreateCorrespondenceRequest
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkCreateCorrespondenceRequest) => {
      const result = await bulkCreateCorrespondencesAction(matterId, data);
      if (!result.success) {
        throw new CorrespondenceValidationError(
          result.error || 'Failed to create correspondences',
          result.validationErrors
        );
      }
      return result;
    },
    onSuccess: (result) => {
      invalidateAfterCorrespondenceMutation(queryClient, matterId);

      const count = result.data!.length;
      toast.success(
        `${count} correspondence${count > 1 ? 's' : ''} created successfully`
      );
    },
    onError: (error: Error) => {
      if (
        error instanceof CorrespondenceValidationError &&
        error.validationErrors
      ) {
        // Show validation errors
        const errors = Object.entries(error.validationErrors)
          .map(
            ([field, messages]) =>
              `${field}: ${(messages as string[]).join(', ')}`
          )
          .join('\n');
        toast.error(`Validation failed:\n${errors}`);
      } else {
        toast.error(error.message || 'Failed to create correspondence');
      }
    },
    ...options
  });
}

/**
 * Hook to update a correspondence
 */
export function useUpdateCorrespondence(
  matterId: number,
  correspondenceId: number,
  options?: UseMutationOptions<
    CorrespondenceActionResult<Correspondence>,
    Error,
    UpdateCorrespondenceRequest
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCorrespondenceRequest) => {
      const result = await updateCorrespondenceAction(
        matterId,
        correspondenceId,
        data
      );
      if (!result.success) {
        throw new CorrespondenceValidationError(
          result.error || 'Failed to update correspondence',
          result.validationErrors
        );
      }
      return result;
    },
    onSuccess: (result) => {
      // Optimistic write so the detail view updates instantly; cascade
      // invalidation handles list/threads/letter-preview/etc.
      queryClient.setQueryData(
        getCorrespondenceCacheKey(matterId, correspondenceId),
        result.data
      );

      invalidateAfterCorrespondenceMutation(
        queryClient,
        matterId,
        correspondenceId
      );

      toast.success('Correspondence updated successfully');
    },
    onError: (error: Error) => {
      if (
        error instanceof CorrespondenceValidationError &&
        error.validationErrors
      ) {
        // Show validation errors
        const errors = Object.entries(error.validationErrors)
          .map(
            ([field, messages]) =>
              `${field}: ${(messages as string[]).join(', ')}`
          )
          .join('\n');
        toast.error(`Validation failed:\n${errors}`);
      } else {
        toast.error(error.message || 'Failed to update correspondence');
      }
    },
    ...options
  });
}

/**
 * Hook to delete a correspondence
 */
export function useDeleteCorrespondence(
  matterId: number,
  options?: UseMutationOptions<CorrespondenceActionResult<void>, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (correspondenceId: number) => {
      const result = await deleteCorrespondenceAction(
        matterId,
        correspondenceId
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete correspondence');
      }
      return result;
    },
    onSuccess: (_, correspondenceId) => {
      queryClient.removeQueries({
        queryKey: getCorrespondenceCacheKey(matterId, correspondenceId)
      });

      invalidateAfterCorrespondenceMutation(
        queryClient,
        matterId,
        correspondenceId
      );

      toast.success('Correspondence deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete correspondence');
    },
    ...options
  });
}

/**
 * Hook to send a correspondence
 */
export function useSendCorrespondence(
  matterId: number,
  options?: UseMutationOptions<
    CorrespondenceActionResult<Correspondence>,
    Error,
    number
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (correspondenceId: number) => {
      const result = await sendCorrespondenceAction(matterId, correspondenceId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to send correspondence');
      }
      return result;
    },
    onSuccess: (result, correspondenceId) => {
      // Optimistic write — invalidation cascade handles the rest
      // (list/tasks/inventory/institutions/discovery/redemption per
      // `getDependencies('correspondence', matterId)`).
      queryClient.setQueryData(
        getCorrespondenceCacheKey(matterId, correspondenceId),
        result.data
      );

      invalidateAfterCorrespondenceMutation(
        queryClient,
        matterId,
        correspondenceId
      );

      // Toast is handled by the workflow that calls this hook
      // to avoid duplicate notifications.

      // Signal UI (e.g., NextStepsCard) to refresh immediately.
      dispatchCorrespondenceWorkflowCompleted({ matterId });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send correspondence');
    },
    ...options
  });
}

// Composite Hooks

/**
 * Hook that combines all correspondence operations for a matter
 */
export function useMatterCorrespondencesService(matterId: number) {
  const queryClient = useQueryClient();

  // Queries
  const correspondencesQuery = useMatterCorrespondences(matterId);

  // Mutations
  const createMutation = useCreateCorrespondence(matterId);
  const bulkCreateMutation = useBulkCreateCorrespondences(matterId);
  const deleteMutation = useDeleteCorrespondence(matterId);
  const sendMutation = useSendCorrespondence(matterId);

  // Helper functions
  const getCorrespondence = useCallback(
    (correspondenceId: number) => {
      const data = queryClient.getQueryData<CorrespondenceListResponse>(
        getCorrespondencesCacheKey(matterId)
      );
      return data?.correspondences.find((c) => c.id === correspondenceId);
    },
    [matterId, queryClient]
  );

  const refreshCorrespondences = useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: getCorrespondencesCacheKey(matterId)
    });
  }, [matterId, queryClient]);

  const createCorrespondence = useCallback(
    async (data: CreateCorrespondenceRequest) => {
      return createMutation.mutateAsync(data);
    },
    [createMutation]
  );

  const bulkCreateCorrespondences = useCallback(
    async (data: BulkCreateCorrespondenceRequest) => {
      return bulkCreateMutation.mutateAsync(data);
    },
    [bulkCreateMutation]
  );

  const updateCorrespondence = useCallback(
    async (correspondenceId: number, data: UpdateCorrespondenceRequest) => {
      // Call the server action directly instead of using the hook inside callback
      const result = await updateCorrespondenceAction(
        matterId,
        correspondenceId,
        data
      );

      if (result.success) {
        invalidateAfterCorrespondenceMutation(
          queryClient,
          matterId,
          correspondenceId
        );
      } else {
        throw new Error(result.error);
      }

      return result;
    },
    [matterId, queryClient]
  );

  const deleteCorrespondence = useCallback(
    async (correspondenceId: number) => {
      return deleteMutation.mutateAsync(correspondenceId);
    },
    [deleteMutation]
  );

  const sendCorrespondence = useCallback(
    async (correspondenceId: number) => {
      return sendMutation.mutateAsync(correspondenceId);
    },
    [sendMutation]
  );

  return {
    // Data
    correspondences: correspondencesQuery.data?.correspondences || [],
    total: correspondencesQuery.data?.total || 0,

    // Loading states
    isLoading: correspondencesQuery.isLoading,
    isError: correspondencesQuery.isError,
    error: correspondencesQuery.error,

    // Operations
    createCorrespondence,
    bulkCreateCorrespondences,
    updateCorrespondence,
    deleteCorrespondence,
    sendCorrespondence,
    getCorrespondence,
    refreshCorrespondences,

    // Mutation states
    isCreating: createMutation.isPending,
    isBulkCreating: bulkCreateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSending: sendMutation.isPending
  };
}
